import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { ifsApi } from "lib/axios";

// ─── Types ───

// Normalized driver as carried on the tractor object in the fuel-optimizer flow.
export interface TractorDriver {
  driver_id: string | null;
  driver_name: string | null;
  phoneNumber: string | null;
}

export interface Tractor {
  id: number | null;
  unit_number: string;
  email_address?: string | null;
  tank_capacity: number;
  MPG: number;
  status?: boolean | number;
  driver_id?: string | null;
  driver_name?: string | null;
  phoneNumber?: string | null;
  fuelLevelInGallons?: number;
  Integration?: {
    Truck?: {
      ID?: string;
    };
  } | null;
  drivers?: TractorDriver[];
}

export interface TractorInput {
  unit: string;
  email_address: string;
  tank_capacity: number;
  status: boolean;
  id: number | null;
  MPG: number;
}

export interface TruckFuelPercent {
  fuelLevelInPercentage: number;
}

export interface TruckDriverAssignment {
  driverAssignment: unknown;
}

// ─── Queries ───

async function getTractorsForDropdown(customerCode: string): Promise<Tractor[]> {
  const response = await ifsApi.get(`/company/${customerCode}/integrated-trucks`);
  return response.data.data;
}

export const integratedTractorsOptions = (customerCode: string) =>
  queryOptions({
    queryKey: ["tractors", "integrated", customerCode],
    queryFn: () => getTractorsForDropdown(customerCode),
  });

async function getTractorsForTable(customerCode: string): Promise<Tractor[]> {
  const response = await ifsApi.get(`/tractors?customerCode=${customerCode}`);
  return response.data.data.unit_numbers;
}

export const tractorsTableOptions = (customerCode: string) =>
  queryOptions({
    queryKey: ["tractors", "table", customerCode],
    queryFn: () => getTractorsForTable(customerCode),
  });

async function getTruckFuelPercent(customerCode: string, externalTruckID: string): Promise<TruckFuelPercent> {
  const response = await ifsApi.get(
    `/company/${customerCode}/integration/trucks/fuel-percent?externalTruckID=${externalTruckID}`
  );
  return response.data.data;
}

export const truckFuelPercentOptions = (customerCode: string, externalTruckID: string) =>
  queryOptions({
    queryKey: ["tractors", "fuel-percent", customerCode, externalTruckID],
    queryFn: () => getTruckFuelPercent(customerCode, externalTruckID),
  });

async function getTruckDriverAssignment(
  customerCode: string,
  externalTruckID: string
): Promise<TruckDriverAssignment> {
  const response = await ifsApi.get(
    `/company/${customerCode}/integration/trucks/driver-assignment?externalTruckID=${externalTruckID}`
  );
  return response.data.data;
}

export const truckDriverAssignmentOptions = (customerCode: string, externalTruckID: string) =>
  queryOptions({
    queryKey: ["tractors", "driver-assignment", customerCode, externalTruckID],
    queryFn: () => getTruckDriverAssignment(customerCode, externalTruckID),
  });

// ─── Mutations ───

// Creates and updates a tractor
async function createTractor(customer: string, tractorData: TractorInput): Promise<void> {
  await ifsApi.post(`/tractors`, { customer, ...tractorData });
}

async function deleteTractor(customer: string, id: number): Promise<void> {
  await ifsApi.delete(`/tractors/${id}`);
}

export function useCreateTractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customer, tractor }: { customer: string; tractor: TractorInput }) =>
      createTractor(customer, tractor),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tractors"] }),
  });
}

export function useDeleteTractor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ customer, id }: { customer: string; id: number }) => deleteTractor(customer, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tractors"] }),
  });
}
